import gql from "graphql-tag";
import {useCallback} from "react";

import {useMutation} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import type {setCollectingInterestsType} from "./__graphql-types__/index.ts";

export const SET_COLLECT_INTERESTS_MUTATION: GqlOperation<
    setCollectingInterestsType["response"],
    setCollectingInterestsType["variables"]
> = gqlOp(gql`
    mutation setCollectingInterests($kaid: String!, $collect: Boolean!) {
        setCollectInterests(kaid: $kaid, collect: $collect) {
            error {
                code
                debugMessage
            }
        }
    }
`);

/**
 * Manage the setting for collecing interests from conversations.
 *
 * @returns A function that takes a kaid and a boolean indicating whether or not
 * the user wants Khanmigo to collect interests from their conversations.
 * When invoked, this function sends a request to update the user's collect
 * interests setting.
 */
export default function useSetCollectInterestsMutation(): (
    kaid: string,
    collect: boolean,
) => Promise<any> {
    const [setCollectInterests] = useMutation(SET_COLLECT_INTERESTS_MUTATION);

    const setCollectInterestsMutation = useCallback(
        async (kaid: string, collect: boolean) => {
            try {
                const result = await setCollectInterests({kaid, collect});

                // @ts-expect-error - TS2339
                if (result.data?.setCollectInterests.error) {
                    // @ts-expect-error - TS2339
                    const error = result.data.setCollectInterests.error.code;
                    throw new KAError(error, Errors.Internal);
                }
            } catch (error: any) {
                // We handle the exception thrown from within the mutation success
                // handler.
                if (error instanceof KAError) {
                    throw error;
                }
                // Otherwise we throw a generic error.
                throw new KAError(
                    "Failed to record setCollectInterest mutation",
                    Errors.Internal,
                );
            }
        },
        [setCollectInterests],
    );

    return setCollectInterestsMutation;
}
